import { useState, useEffect, useRef } from "react";
import { Excalidraw, MainMenu } from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";

// BirdiesBrains — Canvas tab
// Step 2: loads this user's board from Supabase on mount, and saves it back
// (debounced) on every change — same load-once / debounced-upsert pattern as
// the tasks sync in App.jsx. One board per user for now.
export default function Canvas({ supabase, user }) {
  // null = still loading from Supabase. We wait for this before rendering
  // Excalidraw, because initialData is only read once, when it first mounts.
  const [initialData, setInitialData] = useState(null);
  const saveTimeout = useRef(null);
  const ready = useRef(false); // used to skip the first onChange (fires on mount)

  // ── Load this user's saved board, once ────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase
      .from("canvas_boards")
      .select("elements, app_state")
      .eq("user_id", user.id)
      .maybeSingle() // returns null (not an error) when the user has no board yet
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error("Canvas load failed:", error.message);
        setInitialData({
          elements: data?.elements || [],
          appState: {
            viewBackgroundColor: data?.app_state?.viewBackgroundColor || "#ffffff",
          },
          scrollToContent: true, // center the view on the saved drawing
        });
      });
    return () => { cancelled = true; };
  }, [supabase, user]);

  // ── Save (debounced) on every change ──────────────────────────────────────
  const handleChange = (elements, appState) => {
    // Excalidraw fires onChange once on mount with the data we just loaded.
    // Skip that one so we don't immediately write it straight back.
    if (!ready.current) { ready.current = true; return; }
    if (!user) return;

    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      const { error } = await supabase.from("canvas_boards").upsert(
        {
          user_id: user.id,
          // Drop deleted elements so the saved board doesn't grow forever
          elements: elements.filter((el) => !el.isDeleted),
          app_state: { viewBackgroundColor: appState.viewBackgroundColor },
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );
      if (error) console.error("Canvas save failed:", error.message);
    }, 1500);
  };

  // If you switch tabs mid-edit, cancel any pending save cleanly
  useEffect(() => () => clearTimeout(saveTimeout.current), []);

  if (!initialData) {
    return (
      <div style={{
        height: "calc(100vh - 100px)", width: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#7878a0", fontFamily: "'DM Sans','Segoe UI',sans-serif",
      }}>
        Loading your canvas…
      </div>
    );
  }

  return (
    // 100px ≈ your header + tab bar. Adjust if the board looks cut off.
    <div style={{ height: "calc(100vh - 100px)", width: "100%" }}>
      {/* Excalidraw has no prop to disable its built-in library sidebar
          trigger (top-right "Library" button), or the "Mermaid to
          Excalidraw" entry in the "More tools" dropdown, in this version —
          hiding both with CSS is the pragmatic option here. Mermaid's menu
          item oddly shares its class and data-testid with "Web Embed" (which
          we want to keep), so it can't be targeted directly — its icon's SVG
          path data is unique, so :has() targets that instead. */}
      <style>{`
        .default-sidebar-trigger { display: none !important; }
        .dropdown-menu-item:has(path[d^="M407.48"]) { display: none !important; }
        /* Removing the item above leaves its plain, class-less "Generate"
           section label orphaned above nothing. Scoped to specifically the
           "More tools" dropdown (identified by its frame-tool button) so
           this can't ever affect the unrelated "Canvas background" label in
           the main menu, which is built the same generic way. */
        .dropdown-menu-container:has([data-testid="toolbar-frame"]) > div[style] { display: none !important; }
      `}</style>
      <Excalidraw
        theme="dark"
        initialData={initialData}
        onChange={handleChange}
      >
        {/* Custom menu — same items Excalidraw shows by default, minus the
            "Excalidraw links" group (GitHub / Follow us / Discord chat). */}
        <MainMenu>
          <MainMenu.DefaultItems.LoadScene />
          <MainMenu.DefaultItems.Export />
          <MainMenu.DefaultItems.SaveAsImage />
          <MainMenu.DefaultItems.SearchMenu />
          <MainMenu.DefaultItems.Help />
          <MainMenu.DefaultItems.ClearCanvas />
          <MainMenu.Separator />
          <MainMenu.DefaultItems.ChangeCanvasBackground />
        </MainMenu>
      </Excalidraw>
    </div>
  );
}